import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const API_BASE = 'https://birdseye-main-326804163209.asia-southeast1.run.app';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,700;1,400&family=Playfair+Display:ital,wght@1,400;1,500&display=swap');

  @keyframes shimmer-sweep {
    0%   { background-position: -300% center; }
    100% { background-position:  300% center; }
  }
  @keyframes blob-drift {
    0%,100% { transform: translate(0,0) scale(1); }
    33%     { transform: translate(30px,20px) scale(1.06); }
    66%     { transform: translate(-20px,30px) scale(0.96); }
  }
  @keyframes float-up {
    0%,100% { transform: translateY(0); }
    50%     { transform: translateY(-8px); }
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  /* ── Button: solid red base + gentle shimmer sweep only ── */
  .shimmer-btn {
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #B84545 0%, #8B2E2E 100%);
    border: none;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    transition: transform .15s ease, box-shadow .2s ease, background .2s ease;
  }
  .shimmer-btn::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(
      100deg,
      transparent 20%,
      rgba(255,255,255,0.13) 50%,
      transparent 80%
    );
    background-size: 300% 100%;
    animation: shimmer-sweep 3.5s ease-in-out infinite;
    border-radius: inherit;
  }
  .shimmer-btn span { position: relative; z-index: 1; font-family: 'DM Sans', sans-serif; }
  .shimmer-btn svg  { position: relative; z-index: 1; }
  .shimmer-btn:hover {
    background: linear-gradient(135deg, #C85050 0%, #9B3535 100%);
    transform: translateY(-1px);
    box-shadow: 0 6px 22px rgba(172,66,66,.45);
  }
  .shimmer-btn:active  { transform: translateY(0) scale(.98); }
  .shimmer-btn:disabled { opacity: .5; pointer-events: none; }

  /* ── Glass card ── */
  .glass-card {
    background: rgba(255,255,255,0.06);
    backdrop-filter: blur(28px) saturate(1.5);
    -webkit-backdrop-filter: blur(28px) saturate(1.5);
    border: 1px solid rgba(255,255,255,0.13);
    box-shadow:
      0 24px 80px rgba(0,0,0,0.4),
      inset 0 1px 0 rgba(255,255,255,0.1);
  }

  /* ── Inputs ── */
  .form-input {
    width: 100%;
    padding: 13px 18px;
    border-radius: 12px;
    border: 1px solid rgba(255,255,255,0.12);
    background: rgba(255,255,255,0.08);
    font-size: 14px;
    font-family: 'DM Sans', sans-serif;
    color: #fff;
    outline: none;
    transition: border-color .2s, box-shadow .2s, background .2s;
  }
  .form-input::placeholder { color: rgba(255,255,255,0.28); }
  .form-input:focus {
    border-color: rgba(255,37,37,0.5);
    box-shadow: 0 0 0 3px rgba(255,37,37,0.1);
    background: rgba(255,255,255,0.11);
  }
  .form-input:disabled { opacity: .5; }

  .divider-line {
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
    border: none;
  }

  .tag-pill {
    padding: 6px 16px;
    border-radius: 9999px;
    border: 1px solid rgba(255,255,255,.14);
    background: rgba(255,255,255,.07);
    color: rgba(255,255,255,.6);
    font-size: 12px;
    font-weight: 500;
    font-family: 'DM Sans', sans-serif;
  }
  .nav-link {
    color: rgba(255,255,255,.72);
    font-size: 14px;
    cursor: pointer;
    transition: color .15s;
    font-family: 'DM Sans', sans-serif;
    text-decoration: none;
  }
  .nav-link:hover { color: #fff; }
  .text-link {
    color: #ff6b6b;
    font-weight: 600;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 13px;
    padding: 0;
    font-family: 'DM Sans', sans-serif;
    transition: color .15s;
  }
  .text-link:hover { color: #ff8585; }
`;

/* ── helpers ── */
const Spinner = () => (
  <svg style={{ width:15, height:15, animation:'_sp .75s linear infinite', flexShrink:0 }} viewBox="0 0 24 24" fill="none">
    <style>{`@keyframes _sp{to{transform:rotate(360deg)}}`}</style>
    <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,.3)" strokeWidth="3"/>
    <path d="M12 2a10 10 0 0110 10" stroke="white" strokeWidth="3" strokeLinecap="round"/>
  </svg>
);

const ErrorBanner = ({ msg }) => (
  <div style={{ marginTop:12, padding:'10px 14px', borderRadius:12, background:'rgba(220,38,38,.18)', border:'1px solid rgba(220,38,38,.3)', color:'#FFAAAA', fontSize:13, fontFamily:'DM Sans, sans-serif' }}>
    {msg}
  </div>
);

const SuccessBanner = ({ msg }) => (
  <div style={{ marginBottom:16, padding:'10px 14px', borderRadius:12, background:'rgba(16,185,129,.15)', border:'1px solid rgba(16,185,129,.3)', color:'#6EE7B7', fontSize:13, fontFamily:'DM Sans, sans-serif' }}>
    {msg}
  </div>
);

const AnimBtn = ({ isLoading, label, loadingLabel = 'Loading…' }) => (
  <button type="submit" disabled={isLoading} className="shimmer-btn"
    style={{ width:'100%', padding:'14px 24px', borderRadius:14, color:'white', fontSize:14, fontWeight:600, marginTop:18, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
    {isLoading ? <><Spinner/><span>{loadingLabel}</span></> : <span>{label}</span>}
  </button>
);

const StepDots = ({ current }) => (
  <div style={{ display:'flex', gap:8, justifyContent:'center', marginBottom:24 }}>
    {[0,1].map(i => (
      <motion.div key={i} style={{ height:5, borderRadius:9999 }}
        animate={{ width: i===current ? 28 : 10, background: i===current ? '#B84545' : 'rgba(255,255,255,.22)' }}
        transition={{ duration:.3, ease:[.4,0,.2,1] }}/>
    ))}
  </div>
);

const cardVariants = {
  enter:  { opacity:0, x:24, scale:.98 },
  center: { opacity:1, x:0,  scale:1   },
  exit:   { opacity:0, x:-24, scale:.98 },
};

/* ── page ── */
const LoginPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');

  const [loginEmail, setLoginEmail]           = useState('');
  const [loginPassword, setLoginPassword]     = useState('');
  const [activationCode, setActivationCode]   = useState('');
  const [name, setName]                       = useState('');
  const [signupEmail, setSignupEmail]         = useState('');
  const [signupPassword, setSignupPassword]   = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');

  const handleActivationChange = (value) => {
    let v = value.toUpperCase().replace(/[^A-Z0-9]/g,'').slice(0,20);
    let f = v.slice(0,5);
    if(v.length>5)  f += '-'+v.slice(5,10);
    if(v.length>10) f += '-'+v.slice(10,15);
    if(v.length>15) f += '-'+v.slice(15,20);
    setActivationCode(f); setError('');
  };

  const switchMode = (m) => { setError(''); setSuccess(''); setMode(m); };

  const handleLogin = async (e) => {
    e.preventDefault(); setIsLoading(true); setError('');
    try {
      const fd = new URLSearchParams();
      fd.append('username', loginEmail); fd.append('password', loginPassword);
      const res  = await fetch(`${API_BASE}/auth/login`, { method:'POST', headers:{'Content-Type':'application/x-www-form-urlencoded'}, body:fd });
      const data = await res.json();
      if(res.ok && data.access_token){
        localStorage.setItem('authToken', data.access_token);
        localStorage.setItem('userEmail', loginEmail);
        navigate('/dashboard');
      } else { setError(data?.detail ?? 'Incorrect email or password.'); }
    } catch { setError('Could not reach the server.'); }
    finally  { setIsLoading(false); }
  };

  const handleActivationNext = (e) => {
    e.preventDefault();
    if(activationCode.length !== 23){ setError('Please enter a valid code (XXXXX-XXXXX-XXXXX-XXXXX).'); return; }
    switchMode('signup-details');
  };

  const handleSignup = async (e) => {
    e.preventDefault(); setError('');
    if(signupPassword !== confirmPassword){ setError('Passwords do not match.'); return; }
    if(signupPassword.length < 6){ setError('Password must be at least 6 characters.'); return; }
    setIsLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/auth/signup`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ name, email:signupEmail, password:signupPassword, activationCode }),
      });
      const data = await res.json();
      if(res.ok){
        const fd = new URLSearchParams();
        fd.append('username',signupEmail); fd.append('password',signupPassword);
        const lr = await fetch(`${API_BASE}/auth/login`, { method:'POST', headers:{'Content-Type':'application/x-www-form-urlencoded'}, body:fd });
        const ld = await lr.json();
        if(lr.ok && ld.access_token){
          localStorage.setItem('authToken', ld.access_token);
          localStorage.setItem('userEmail', signupEmail);
          navigate('/dashboard');
        } else { setSuccess('Account created! Please sign in.'); switchMode('login'); }
      } else { setError(data?.detail ?? 'Signup failed.'); }
    } catch { setError('Could not reach the server.'); }
    finally  { setIsLoading(false); }
  };

  return (
    <motion.div
      style={{ minHeight:'100vh', display:'flex', flexDirection:'column', position:'relative', overflow:'hidden', fontFamily:'DM Sans, sans-serif', background:'#060a14', color:'#fff' }}
      initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
    >
      <style>{STYLES}</style>

      {/* ── BG ── */}
      <div style={{ position:'fixed', inset:0, zIndex:0, background:'radial-gradient(ellipse 80% 60% at 20% 110%, rgba(30,60,120,0.7) 0%, transparent 60%), radial-gradient(ellipse 60% 50% at 80% -10%, rgba(15,35,90,0.8) 0%, transparent 55%), linear-gradient(175deg, #0a1628 0%, #0d1f44 35%, #112050 55%, #0b1630 100%)' }} />
      <video autoPlay loop muted playsInline style={{ position:'fixed', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', zIndex:0, opacity:0.3, pointerEvents:'none' }}>
        <source src="/loopsign.mp4" type="video/mp4" />
      </video>
      <div style={{ position:'fixed', width:700, height:300, top:-80, left:-100, borderRadius:'50%', background:'radial-gradient(ellipse, rgba(80,110,180,0.18) 0%, transparent 70%)', filter:'blur(40px)', pointerEvents:'none' }} />
      <div style={{ position:'fixed', width:500, height:250, top:'30%', right:-80, borderRadius:'50%', background:'radial-gradient(ellipse, rgba(60,90,160,0.15) 0%, transparent 70%)', filter:'blur(40px)', pointerEvents:'none' }} />
      <div style={{ position:'fixed', width:600, height:200, bottom:'5%', left:'20%', borderRadius:'50%', background:'radial-gradient(ellipse, rgba(40,70,140,0.12) 0%, transparent 70%)', filter:'blur(40px)', pointerEvents:'none' }} />

      {/* ── NAV ── */}
      <nav style={{ position:'fixed', top:0, left:0, right:0, zIndex:100, padding:'0 40px', height:68, display:'flex', alignItems:'center', background:'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, transparent 100%)' }}>
        <div style={{ width:'100%', maxWidth:1120, margin:'0 auto', display:'grid', gridTemplateColumns:'auto 1fr auto', gap:24, alignItems:'center' }}>
          <button style={{ background:'none', border:'none', padding:0, display:'flex', alignItems:'center', gap:8, cursor:'pointer' }} onClick={() => navigate('/') }>
            <img src="/images/logo.png" alt="" style={{ height:24 }} />
            <img src="/images/logo-text.png" alt="Birdseye" style={{ height:20 }} />
          </button>

          <div style={{ display:'flex', justifyContent:'center', gap:32 }}>
            <a href="#features" className="nav-link">Features</a>
            <a href="#how-it-works" className="nav-link">How it works</a>
            <a href="#pricing" className="nav-link">Pricing</a>
            <a href="#contact-us" className="nav-link">Contact us</a>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <button style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.65)', fontFamily:'DM Sans, sans-serif', fontSize:13, display:'flex', alignItems:'center', gap:8 }} onClick={() => navigate('/login')}>Log in</button>
            <button style={{ padding:'8px 22px', borderRadius:100, border:'none', cursor:'pointer', fontFamily:'DM Sans, sans-serif', fontSize:13, fontWeight:500, color:'#fff', background:'linear-gradient(135deg, #FF2525, #8B1010)', boxShadow:'0 0 22px rgba(255,37,37,0.35)' }} onClick={() => navigate('/signup')}>Get Started</button>
          </div>
        </div>
      </nav>
      <div style={{ height:68 }} />

      {/* ── BODY: exact 50/50, fills remaining height ── */}
      <div style={{ position:'relative', zIndex:10, flex:1, display:'grid', gridTemplateColumns:'1fr 1fr', minHeight:0, padding:'24px 40px 40px', maxWidth:1120, width:'100%', margin:'0 auto', gap:64 }}>

        {/* LEFT — branding */}
        <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', padding:'48px 24px' }}>

          {/* icon + text: NO gap div, just the image then immediately the text */}
          <motion.div style={{ display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center', maxWidth:400 }}
            initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:.2, duration:.55 }}>
            <img src="/images/loginlogo.png" alt="Birdseye"
              style={{ width:148, height:'auto', marginBottom:14, animation:'float-up 6s ease-in-out infinite', filter:'drop-shadow(0 16px 36px rgba(172,66,66,.5)) drop-shadow(0 4px 12px rgba(0,0,0,.35))' }}/>
            <h1 style={{ fontSize:'clamp(30px, 3.5vw, 46px)', fontWeight:500, color:'white', lineHeight:1.1, marginBottom:12, letterSpacing:-1 }}>
              See everything.<br/>
              <span style={{ fontFamily:"'Playfair Display', serif", fontStyle:'italic', fontWeight:400 }}>
                Miss nothing.
              </span>
            </h1>
            <p style={{ color:'rgba(255,255,255,.55)', fontSize:15, lineHeight:1.6, margin:0 }}>
              Let's make sure no flock goes undetected.
            </p>
          </motion.div>

          <motion.div style={{ display:'flex', gap:10, marginTop:32, flexWrap:'wrap', justifyContent:'center' }}
            initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:.5 }}>
            {['Real-time data','Smart alerts', 'Clear dashboards'].map(tag => (
              <span key={tag} className="tag-pill">{tag}</span>
            ))}
          </motion.div>
        </div>

        {/* RIGHT — tall glass card, small vertical margin */}
        <div style={{ display:'flex', alignItems:'center', padding:'20px 0' }}>
        <div className="glass-card" style={{ width:'100%', borderRadius:24, padding:'40px 36px', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center' }}>
          <div style={{ width:'100%', maxWidth:400 }}>
            <AnimatePresence mode="wait">
              <motion.div key={mode} variants={cardVariants} initial="enter" animate="center" exit="exit"
                transition={{ duration:.22, ease:[.4,0,.2,1] }}>

                {(mode==='signup-code'||mode==='signup-details') && (
                  <StepDots current={mode==='signup-code' ? 0 : 1}/>
                )}

                <h2 style={{ fontSize:22, fontWeight:600, color:'#fff', marginBottom:6, letterSpacing:-0.4, textAlign:'center' }}>
                  {mode==='login' ? 'Welcome back.' : mode==='signup-code' ? 'Activation Code.' : 'Create Account.'}
                </h2>
                <p style={{ fontSize:13, color:'rgba(255,255,255,.5)', marginBottom:28, textAlign:'center' }}>
                  {mode==='login'         ? 'Sign in to continue to Birdseye.'
                   : mode==='signup-code' ? 'Enter the code provided by Birdseye.'
                   :                        'Fill in your details to get started.'}
                </p>

                {success && <SuccessBanner msg={success}/>}

                {/* LOGIN */}
                {mode==='login' && (
                  <form onSubmit={handleLogin}>
                    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                      <input type="email" placeholder="Email address" value={loginEmail}
                        onChange={e=>{setLoginEmail(e.target.value);setError('');}}
                        className="form-input" disabled={isLoading} autoFocus/>
                      <input type="password" placeholder="Password" value={loginPassword}
                        onChange={e=>{setLoginPassword(e.target.value);setError('');}}
                        className="form-input" disabled={isLoading}/>
                    </div>
                    {error && <ErrorBanner msg={error}/>}
                    <AnimBtn isLoading={isLoading} label="Sign In" loadingLabel="Signing in…"/>
                    <hr className="divider-line" style={{ margin:'22px 0' }}/>
                    <p style={{ textAlign:'center', fontSize:13, color:'rgba(255,255,255,.42)' }}>
                      Don't have an account?{' '}
                      <button type="button" className="text-link" onClick={()=>switchMode('signup-code')}>Sign up</button>
                    </p>
                  </form>
                )}

                {/* SIGNUP step 1 */}
                {mode==='signup-code' && (
                  <form onSubmit={handleActivationNext}>
                    <input type="text" placeholder="XXXXX-XXXXX-XXXXX-XXXXX" value={activationCode}
                      onChange={e=>handleActivationChange(e.target.value)}
                      className="form-input" disabled={isLoading} autoFocus
                      style={{ letterSpacing:'0.15em', textAlign:'center', fontFamily:'monospace' }}/>
                    {error && <ErrorBanner msg={error}/>}
                    <AnimBtn isLoading={isLoading} label="Continue →"/>
                    <hr className="divider-line" style={{ margin:'22px 0' }}/>
                    <p style={{ textAlign:'center', fontSize:13, color:'rgba(255,255,255,.42)' }}>
                      Already have an account?{' '}
                      <button type="button" className="text-link" onClick={()=>switchMode('login')}>Sign in</button>
                    </p>
                  </form>
                )}

                {/* SIGNUP step 2 */}
                {mode==='signup-details' && (
                  <form onSubmit={handleSignup}>
                    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                      {[
                        {type:'text',     ph:'Full name',        val:name,            set:setName},
                        {type:'email',    ph:'Email address',    val:signupEmail,     set:setSignupEmail},
                        {type:'password', ph:'Password',         val:signupPassword,  set:setSignupPassword},
                        {type:'password', ph:'Confirm password', val:confirmPassword, set:setConfirmPassword},
                      ].map(({type,ph,val,set},i) => (
                        <input key={ph} type={type} placeholder={ph} value={val}
                          onChange={e=>{set(e.target.value);setError('');}}
                          className="form-input" disabled={isLoading} autoFocus={i===0}/>
                      ))}
                    </div>
                    {error && <ErrorBanner msg={error}/>}
                    <AnimBtn isLoading={isLoading} label="Create Account" loadingLabel="Creating account…"/>
                    <hr className="divider-line" style={{ margin:'22px 0' }}/>
                    <p style={{ textAlign:'center', fontSize:13, color:'rgba(255,255,255,.42)' }}>
                      <button type="button" className="text-link" onClick={()=>switchMode('signup-code')}>← Back</button>
                    </p>
                  </form>
                )}

              </motion.div>
            </AnimatePresence>
          </div>
          </div>
        </div>

      </div>
    </motion.div>
  );
};

export default LoginPage;
